'use client';

import React from 'react';
import { VisualizationWrapper } from './visualization-wrapper';

interface ComparisonTableProps {
  element: {
    props: {
      title: string;
      items: string[];
      criteria: Array<{
        name: string;
        description?: string;
      }>;
      values: Array<Array<{
        value: string;
        rating?: 'excellent' | 'good' | 'neutral' | 'poor';
      }>>;
    };
    raw?: boolean;
    visualizationId?: string;
  };
}

const ratingStyles: Record<string, { bg: string; border: string; text: string }> = {
  excellent: { bg: 'bg-emerald-50 dark:bg-emerald-950/30', border: 'border-emerald-200 dark:border-emerald-800', text: 'text-emerald-700 dark:text-emerald-300' },
  good: { bg: 'bg-blue-50 dark:bg-blue-950/30', border: 'border-blue-200 dark:border-blue-800', text: 'text-blue-700 dark:text-blue-300' },
  neutral: { bg: 'bg-gray-50 dark:bg-gray-900/30', border: 'border-gray-200 dark:border-gray-700', text: 'text-gray-600 dark:text-gray-400' },
  poor: { bg: 'bg-red-50 dark:bg-red-950/30', border: 'border-red-200 dark:border-red-800', text: 'text-red-700 dark:text-red-300' },
};

const ratingDot: Record<string, string> = {
  excellent: 'bg-emerald-500',
  good: 'bg-blue-500',
  neutral: 'bg-gray-400',
  poor: 'bg-red-500',
};

function ComparisonTableContent({ items, criteria, values }: {
  items: string[];
  criteria: ComparisonTableProps['element']['props']['criteria'];
  values: ComparisonTableProps['element']['props']['values'];
}) {
  return (
    <div className="w-full flex items-start justify-center p-2 sm:p-4">
      {/* Horizontal scroll wrapper for mobile */}
      <div className="w-full overflow-x-auto">
        <div
          className="grid gap-px bg-border rounded-lg overflow-hidden border border-border"
          style={{
            gridTemplateColumns: `minmax(120px, 1.2fr) repeat(${items.length}, minmax(140px, 1fr))`,
            minWidth: 'min-content',
          }}
        >
        {/* Header row */}
        <div className="bg-muted/60 px-4 py-3 font-semibold text-sm text-muted-foreground" />
        {items.map((item, i) => (
          <div key={i} className="bg-muted/60 px-4 py-3 text-center font-semibold text-sm">
            {item}
          </div>
        ))}

        {/* Data rows */}
        {criteria.map((criterion, rowIndex) => (
          <React.Fragment key={rowIndex}>
            <div className="bg-background px-4 py-3 flex flex-col justify-center">
              <span className="font-medium text-sm">{criterion.name}</span>
              {criterion.description && (
                <span className="text-xs text-muted-foreground mt-0.5 leading-tight">
                  {criterion.description}
                </span>
              )}
            </div>
            {items.map((_, colIndex) => {
              const cell = values[rowIndex]?.[colIndex];
              const rating = cell?.rating;
              const styles = rating ? ratingStyles[rating] : null;

              return (
                <div
                  key={colIndex}
                  className={`px-3 py-3 flex items-center justify-center text-center ${
                    styles ? `${styles.bg}` : 'bg-background'
                  }`}
                >
                  <span className={`text-sm leading-snug ${styles ? styles.text : 'text-foreground'}`}>
                    {rating && (
                      <span className={`inline-block w-1.5 h-1.5 rounded-full ${ratingDot[rating]} mr-1.5 align-middle`} />
                    )}
                    {cell?.value || '-'}
                  </span>
                </div>
              );
            })}
          </React.Fragment>
        ))}
        </div>
      </div>
    </div>
  );
}

export function ComparisonTable({ element }: ComparisonTableProps) {
  if (!element?.props?.items?.length || !element?.props?.criteria?.length) {
    return <div className="p-4 border rounded-lg bg-muted/30 text-muted-foreground">Loading comparison...</div>;
  }

  const { title, items, criteria, values } = element.props;

  if (element.raw) {
    return (
      <div className="w-full h-full">
        <ComparisonTableContent items={items} criteria={criteria} values={values} />
      </div>
    );
  }

  return (
    <VisualizationWrapper
      title={title}
      minHeight={200}
      flexHeight
      exportData={{
        type: 'comparison-table',
        content: element.props
      }}
      visualizationId={element.visualizationId}
    >
      <ComparisonTableContent items={items} criteria={criteria} values={values} />
    </VisualizationWrapper>
  );
}
